/**
 * Hook to detect data disappearance events
 *
 * Use this hook in components that display data to automatically
 * detect when data goes from populated to empty state.
 */

import { useEffect, useRef } from 'react';
import { emitDataDisappearedEvent } from '../services/dynatrace/dynatrace-custom-events';
import { UseDataDisappearanceDetectorOptions } from '../services/dynatrace/types';

export const useDataDisappearanceDetector = <T>({
	data,
	screenName,
	getDisappearanceReason,
	skipInitialEmpty = true,
	enabled = true,
}: UseDataDisappearanceDetectorOptions<T>): void => {
	const previousDataRef = useRef<T[] | null | undefined>(undefined);
	const isFirstRenderRef = useRef(true);

	useEffect(() => {
		if (!enabled) return;

		// Skip if this is the first render and skipInitialEmpty is true
		if (isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			previousDataRef.current = data;

			// If initial state is already populated, record it
			if (skipInitialEmpty) {
				return;
			}
		}

		// Detect data disappearance
		const previousLength = previousDataRef.current?.length ?? 0;
		const currentLength = data?.length ?? 0;

		if (previousLength > 0 && currentLength === 0) {
			// Data disappeared!
			try {
				const reason = getDisappearanceReason?.() || 'unknown';

				emitDataDisappearedEvent({
					screen: screenName,
					previousRecordCount: previousLength,
					reason,
					route: window.location.pathname,
					timestamp: Date.now(),
				});
			} catch (error) {
				console.error(
					'[DataDisappearanceDetector] Error emitting disappearance event',
					error,
				);
				// Don't re-throw - we don't want to break the component
			}
		}

		// Update previous data reference
		previousDataRef.current = data;
	}, [data, screenName, getDisappearanceReason, skipInitialEmpty, enabled]);
};
